import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  MessageCircle, MessageSquareText, Pencil, Plus, Trash2, Send, Loader2, X, Check, Sparkles,
} from "lucide-react";
import WaAiComposer from "@/components/WaAiComposer";
import { api } from "@/lib/api";
import { waLink, smsLink, buzz } from "@/lib/mobile";
import { renderTemplate } from "@/pages/WaTemplatesPage";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CHANNELS = {
  wa:  { label: "WhatsApp", color: "#25D366", icon: MessageCircle },
  sms: { label: "SMS",      color: "#0284C7", icon: MessageSquareText },
};

function TplForm({ initial, onSave, onCancel, saving }) {
  const [name, setName] = useState(initial?.name || "");
  const [text, setText] = useState(initial?.text || "");
  return (
    <div className="p-3 bg-[#FAFBFC] border border-[#B7955B]/40 rounded-xl space-y-2 animate-fade-in-fast">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nome (es. Non risponde)"
        data-testid="quickmsg-name-input"
        className="w-full text-[13px] font-semibold bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-[#B7955B]"
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Testo del messaggio…"
        rows={3}
        data-testid="quickmsg-text-input"
        className="w-full text-[13px] bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-[#B7955B] resize-none"
      />
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] text-slate-400">Segnaposto:</span>
        {["{nome}", "{cognome}", "{agent}"].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setText((t) => t + (t.endsWith(" ") || !t ? "" : " ") + p)}
            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#F5EFE3] text-[#8a6e3d] active:scale-95"
          >
            {p}
          </button>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave({ name: name.trim(), text: text.trim() })}
          disabled={!name.trim() || !text.trim() || saving}
          data-testid="quickmsg-save-tpl"
          className="flex-1 py-2 rounded-lg bg-[#0A192F] text-white text-[12.5px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Salva
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-slate-200 text-slate-500 text-[12.5px] font-semibold"
        >
          Annulla
        </button>
      </div>
    </div>
  );
}

export default function QuickMsgSheet({ client, open, onOpenChange }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("wa");
  const [manage, setManage] = useState(false);
  const [editingId, setEditingId] = useState(null); // id | "new" | null
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (!open) { setManage(false); setEditingId(null); return; }
    (async () => {
      setLoading(true);
      try {
        let r = await api.get("/wa-templates");
        let items = r.data?.items || [];
        if (items.length === 0) {
          await api.post("/wa-templates/seed-defaults").catch(() => {});
          r = await api.get("/wa-templates");
          items = r.data?.items || [];
        }
        setTemplates(items);
      } catch {} finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!client) return null;
  const ctx = { nome: client.nome || "", cognome: client.cognome || "", agent: user?.name || "" };
  const fullName = `${client.cognome || ""} ${client.nome || ""}`.trim();
  const ch = CHANNELS[channel];

  const send = (tpl) => {
    const text = renderTemplate(tpl.text, ctx);
    buzz();
    if (channel === "wa") {
      window.open(waLink(client.telefono, text), "_blank", "noopener,noreferrer");
    } else {
      window.location.href = smsLink(client.telefono, text);
    }
    if (client.id) {
      api.post(`/clients/${client.id}/updates`, {
        text: `${channel === "wa" ? "📱 WhatsApp" : "💬 SMS"} inviato (template: ${tpl.name})`,
      }).catch(() => {});
    }
    onOpenChange(false);
  };

  const saveTpl = async (payload) => {
    setSaving(true);
    try {
      if (editingId === "new") {
        const r = await api.post("/wa-templates", payload);
        setTemplates((prev) => [...prev, r.data]);
      } else {
        const r = await api.patch(`/wa-templates/${editingId}`, payload);
        setTemplates((prev) => prev.map((t) => (t.id === editingId ? r.data : t)));
      }
      setEditingId(null);
      toast.success("Messaggio salvato");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const delTpl = async (tpl) => {
    try {
      await api.delete(`/wa-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Messaggio eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 rounded-t-2xl max-h-[88vh] flex flex-col w-full sm:max-w-[540px] mx-auto gap-0 border-0"
        data-testid="quickmsg-sheet"
      >
        <SheetTitle className="sr-only">Invia messaggio preimpostato</SheetTitle>
        {/* Handle */}
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow mb-0.5" style={{ fontSize: 9 }}>Messaggio rapido</p>
              <h3 className="font-serif text-[18px] text-slate-900 leading-tight truncate">{fullName || "Cliente"}</h3>
              {client.telefono && <p className="text-[11.5px] text-slate-400 tabular-nums">{client.telefono}</p>}
            </div>
            <button
              type="button"
              onClick={() => { setManage((v) => !v); setEditingId(null); }}
              data-testid="quickmsg-manage-btn"
              title={manage ? "Fine modifica" : "Modifica messaggi preimpostati"}
              className={`p-2 rounded-lg transition-colors shrink-0 mr-7 ${manage ? "bg-[#0A192F] text-[#D4AB6E]" : "bg-slate-100 text-slate-500"}`}
            >
              {manage ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
            </button>
          </div>

          {!manage && (
            <button
              type="button"
              onClick={() => { setAiOpen(true); buzz(); }}
              data-testid="quickmsg-ai-btn"
              className="w-full mt-3 py-2.5 rounded-xl text-[13px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.99]"
              style={{ background: "linear-gradient(135deg, #0A192F 0%, #274472 100%)", border: "1px solid #B7955B" }}
            >
              <Sparkles className="w-4 h-4" style={{ color: "#D4AB6E" }} /> Scrivi con AI (su misura per {client.nome || "il cliente"})
            </button>
          )}
          {/* Canale */}
          {!manage && (
            <div className="grid grid-cols-2 gap-1.5 mt-3 p-1 rounded-xl bg-slate-100">
              {Object.entries(CHANNELS).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = channel === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setChannel(key); buzz(); }}
                    data-testid={`quickmsg-channel-${key}`}
                    className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-semibold transition-all"
                    style={active ? { background: cfg.color, color: "#fff", boxShadow: `0 2px 8px ${cfg.color}55` } : { color: "#64748B" }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.2} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          )}
          {manage && (
            <p className="text-[11px] text-slate-400 mt-2">
              I messaggi sono condivisi con il team · li trovi anche in “Campagne WA → Messaggi”
            </p>
          )}
        </div>

        {/* Lista template */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-2">
          {loading ? (
            [1, 2, 3].map((i) => <div key={i} className="skeleton h-16 w-full rounded-xl" />)
          ) : (
            <>
              {templates.map((t) =>
                editingId === t.id ? (
                  <TplForm key={t.id} initial={t} onSave={saveTpl} onCancel={() => setEditingId(null)} saving={saving} />
                ) : (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => (manage ? setEditingId(t.id) : send(t))}
                    data-testid={`quickmsg-tpl-${t.id}`}
                    className="w-full text-left p-3 bg-white border border-[#E8ECF2] rounded-xl flex items-center gap-3 active:scale-[0.99] transition-transform cursor-pointer hover:border-slate-300"
                    style={{ boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-800">{t.name}</p>
                      <p className="text-[12px] text-slate-500 leading-snug mt-0.5" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {renderTemplate(t.text, ctx)}
                      </p>
                    </div>
                    {manage ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="p-2 rounded-lg bg-slate-100 text-slate-500"><Pencil className="w-3.5 h-3.5" /></span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); delTpl(t); }}
                          data-testid={`quickmsg-del-${t.id}`}
                          className="p-2 rounded-lg bg-rose-50 text-rose-500 active:scale-95"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: `${ch.color}1A`, color: ch.color }}
                      >
                        <Send className="w-4 h-4" strokeWidth={2.2} />
                      </div>
                    )}
                  </div>
                )
              )}

              {editingId === "new" ? (
                <TplForm onSave={saveTpl} onCancel={() => setEditingId(null)} saving={saving} />
              ) : manage ? (
                <button
                  type="button"
                  onClick={() => setEditingId("new")}
                  data-testid="quickmsg-new-btn"
                  className="w-full py-3 rounded-xl border-2 border-dashed border-[#B7955B]/40 text-[#8a6e3d] text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99]"
                >
                  <Plus className="w-4 h-4" /> Nuovo messaggio
                </button>
              ) : null}

              {templates.length === 0 && !manage && (
                <div className="py-8 text-center">
                  <p className="text-[13px] text-slate-500">Nessun messaggio preimpostato.</p>
                  <button
                    type="button"
                    onClick={() => { setManage(true); setEditingId("new"); }}
                    className="mt-2 text-[13px] font-semibold text-[#8a6e3d] underline"
                  >
                    Crea il primo messaggio
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
      <WaAiComposer open={aiOpen} onClose={() => setAiOpen(false)} client={client} onSent={() => onOpenChange(false)} />
    </Sheet>
  );
}
